import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { Modal } from '../../components/Modal';
import { agente, type IdentidadAgente } from '../../lib/agentes';
import ChatAndres from '../ChatAndres';

/**
 * Tab Entrenamiento — aquí se entrena el conocimiento de cada agente con el ciclo
 * Editar → Probar → Validar → Activar (stepper). Dos capas editables:
 *   · Oficio (brief): cómo piensa el agente, para todas las fuentes.
 *   · Contrato base: identidad y reglas duras (gateado por contratos_editables).
 * El banco de pruebas corre el agente aislado con insumos reales, sin tocar lo activo.
 */

type Tipo = 'brief' | 'contrato';
type Estado = 'borrador' | 'probada' | 'activa' | 'archivada';

interface VersionPrompt {
  id: string;
  agent_code: string;
  tipo: Tipo;
  version: number;
  contenido: string;
  estado: Estado;
  nota: string | null;
  created_at: string;
  activated_at: string | null;
  autor_email?: string | null;
}

interface AgenteEnt {
  agent_code: string;
  display_name: string | null;
  oficio: string | null;
  provider_code: string | null;
  model: string | null;
  context_brief: string | null;
  contrato_base: string;
  versiones: VersionPrompt[];
}

interface InsumoItem {
  id: string;
  title: string | null;
  organism: string | null;
  publication_date: string | null;
  priority: string | null;
  clasificado: boolean;
  tiene_borrador: boolean;
}

/** Los 3 más nuevos del sistema por propósito — nunca ejemplos ni data que ya no existe. */
interface Insumos {
  fuentes: { id: string; code: string; name: string }[];
  items_recientes: InsumoItem[];
  items_clasificados: InsumoItem[];
  items_con_borrador: InsumoItem[];
}

interface TestJob {
  id: string;
  status: 'queued' | 'running' | 'done' | 'error';
  stage: string | null;
  error: string | null;
  detail: {
    insumo?: Record<string, unknown>;
    result?: {
      output: unknown;
      ms: number;
      meta: {
        model?: string;
        tokensIn?: number;
        tokensOut?: number;
        tokensReasoning?: number | null;
        estCost?: number;
        latencyMs?: number;
        repairs?: number;
        reasoning?: string | null;
        providerCode?: string;
      };
    };
  } | null;
}

const nf = new Intl.NumberFormat('es-CL');

/** Fecha de ayer en texto ("14 de julio") — el placeholder de N1 jamás cita fechas inventadas. */
function fechaAyer(): string {
  const d = new Date(Date.now() - 86_400_000);
  return `${d.getDate()} de ${['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'][d.getMonth()]}`;
}
const usd = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 4 });
const fmtFecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });

const ESTADO_CHIP: Record<Estado, string> = {
  borrador: 'bg-slate-100 text-slate-700 ring-slate-300',
  probada: 'bg-celeste/20 text-azul-profundo ring-celeste/60',
  activa: 'bg-emerald-50 text-emerald-700 ring-emerald-300',
  archivada: 'bg-slate-50 text-gris ring-slate-200',
};

const ESTADO_LABEL: Record<Estado, string> = {
  borrador: 'Borrador',
  probada: 'Probada',
  activa: 'Activa',
  archivada: 'Archivada',
};

const ETAPA_PRUEBA: Record<string, string> = {
  insumo: 'Preparando el insumo real…',
  agente: 'trabajando sobre el insumo…',
  done: 'Prueba completada',
};

/** Stepper del ciclo de entrenamiento de la versión en trabajo. */
function StepperCiclo({ trabajo, hayActiva }: { trabajo: VersionPrompt | null; hayActiva: boolean }) {
  // Índice del último paso completado: Editar(0) → Probar(1) → Validar(2) → Activar(3)
  const done = trabajo ? (trabajo.estado === 'probada' ? 1 : 0) : hayActiva ? 3 : -1;
  const pasos = [
    { label: 'Editar', detalle: trabajo ? `v${trabajo.version} en trabajo` : hayActiva ? 'versión activa' : 'sin versión' },
    { label: 'Probar', detalle: trabajo?.estado === 'probada' ? 'al menos 1 prueba OK' : 'banco de pruebas' },
    { label: 'Validar', detalle: 'automática en Fase 2' },
    { label: 'Activar', detalle: trabajo?.estado === 'probada' ? 'lista para activar' : 'requiere prueba' },
  ];
  return (
    <ol className="flex min-w-[480px] items-center" aria-label="Ciclo de entrenamiento">
      {pasos.map((p, i) => {
        const esValidar = i === 2;
        const estado =
          i <= done ? 'done' : i === done + 1 ? (esValidar && trabajo?.estado === 'probada' ? 'manual' : 'activo') : 'pendiente';
        // Validar aún no corre solo: cuando la versión está probada se salta con confirmación manual.
        const saltado = esValidar && done >= 1 && done < 3;
        return (
          <li key={p.label} className={`flex items-center ${i < pasos.length - 1 ? 'flex-1' : ''}`}>
            <div className="flex flex-col items-center gap-1">
              <span
                className={`relative flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-medium transition-colors duration-300 ${
                  estado === 'done'
                    ? 'bg-azul-profundo text-white'
                    : saltado || estado === 'manual'
                      ? 'bg-amber-50 text-amber-700 ring-2 ring-amber-300'
                      : estado === 'activo'
                        ? 'bg-white text-azul-profundo ring-2 ring-azul-profundo'
                        : 'bg-slate-100 text-gris'
                }`}
              >
                {estado === 'done' ? (
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m5 13 4 4L19 7" />
                  </svg>
                ) : (
                  i + 1
                )}
                {estado === 'activo' && (
                  <span aria-hidden="true" className="absolute -bottom-1 h-[3px] w-5 rounded-full bg-gradient-to-r from-azul-profundo to-celeste" />
                )}
              </span>
              <span className={`whitespace-nowrap text-[11px] font-medium ${estado === 'pendiente' ? 'text-gris' : 'text-azul-noche'}`}>{p.label}</span>
              <span className="whitespace-nowrap text-[10px] font-normal text-gris">{p.detalle}</span>
            </div>
            {i < pasos.length - 1 && <span aria-hidden="true" className={`mx-2 mb-8 h-px flex-1 ${i < done ? 'bg-azul-profundo' : 'bg-slate-200'}`} />}
          </li>
        );
      })}
    </ol>
  );
}

/** Micro-indicador de 4 puntos del ciclo, para la fila de agentes. */
function MicroCiclo({ trabajo, hayActiva }: { trabajo: VersionPrompt | null; hayActiva: boolean }) {
  const done = trabajo ? (trabajo.estado === 'probada' ? 1 : 0) : hayActiva ? 3 : -1;
  return (
    <span className="flex items-center gap-1" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${
            i <= done ? 'bg-azul-profundo' : i === done + 1 && trabajo ? 'bg-celeste' : 'bg-slate-200'
          }`}
        />
      ))}
    </span>
  );
}

type MetaPrueba = NonNullable<NonNullable<TestJob['detail']>['result']>['meta'];

/** Resultado completo de una prueba (chips + razonamiento + salida). Se usa en el modal y en el panel. */
function ResultadoPrueba({ prueba, meta }: { prueba: TestJob; meta: MetaPrueba | undefined }) {
  if (prueba.status !== 'done' || !prueba.detail?.result) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <span className="chip bg-emerald-50 text-emerald-700 ring-emerald-300">✓ Prueba OK</span>
        {typeof prueba.detail?.insumo?.fecha === 'string' && (
          <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">escaneo: {prueba.detail.insumo.fecha}</span>
        )}
        {typeof prueba.detail?.insumo?.rango === 'string' && (
          <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">escaneo: {prueba.detail.insumo.rango}</span>
        )}
        {typeof prueba.detail?.insumo?.paginas === 'number' && (
          <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">{prueba.detail.insumo.paginas} página{prueba.detail.insumo.paginas === 1 ? '' : 's'} leída{prueba.detail.insumo.paginas === 1 ? '' : 's'}</span>
        )}
        {typeof prueba.detail?.insumo?.mensaje === 'string' && prueba.detail.insumo.mensaje && (
          <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60" title={prueba.detail.insumo.mensaje}>con mensaje de prueba</span>
        )}
        {meta?.model && <span className="chip bg-slate-100 text-slate-700 ring-slate-300">{meta.model}</span>}
        <span className="chip bg-slate-100 text-slate-700 ring-slate-300">
          {nf.format((meta?.tokensIn ?? 0) + (meta?.tokensOut ?? 0))} tokens{meta?.tokensReasoning ? ` (+${nf.format(meta.tokensReasoning)} pensamiento)` : ''}
        </span>
        <span className="chip bg-slate-100 text-slate-700 ring-slate-300">{usd.format(Number(meta?.estCost ?? 0))}</span>
        <span className="chip bg-slate-100 text-slate-700 ring-slate-300">{nf.format(meta?.latencyMs ?? 0)} ms</span>
        {(meta?.repairs ?? 0) > 0 && <span className="chip bg-amber-50 text-amber-700 ring-amber-300">{meta?.repairs} reparación{(meta?.repairs ?? 0) > 1 ? 'es' : ''}</span>}
      </div>
      <div>
        <p className="text-xs font-medium text-azul-noche">Qué piensa</p>
        {meta?.reasoning ? (
          <p className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap text-xs font-normal leading-relaxed text-texto">{meta.reasoning}</p>
        ) : (
          <p className="mt-1 text-xs font-normal text-gris">
            {meta?.tokensReasoning
              ? `El modelo razonó (${nf.format(meta.tokensReasoning)} tokens), pero el proveedor no expone ese texto con salida estructurada.`
              : 'El proveedor no expone el razonamiento con salida estructurada; con un conector Claude verías el pensamiento completo.'}
          </p>
        )}
      </div>
      <div>
        <p className="text-xs font-medium text-azul-noche">Qué devolvió</p>
        <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-3 font-mono text-[11px] leading-relaxed text-texto ring-1 ring-inset ring-slate-200">
          {JSON.stringify(prueba.detail.result.output, null, 2)}
        </pre>
      </div>
    </>
  );
}

/** Frases que rotan mientras el agente trabaja (escena del modal de prueba). */
const FRASES_TRABAJO = [
  'está leyendo el insumo',
  'está pensando',
  'está contrastando con su oficio',
  'está estructurando la salida',
];

/**
 * Modal bloqueante de la prueba: escena del agente trabajando (avatar con anillo que
 * respira + puntos de pensamiento + mini-etapas Insumo→Trabajo→Salida) y, al terminar,
 * el resultado completo. Mientras corre no se cierra por Escape/overlay; queda el
 * escape "Ocultar" (la prueba sigue y el resultado aparece igual en el panel).
 */
function ModalPrueba({
  prueba,
  meta,
  identidad,
  onCerrar,
}: {
  prueba: TestJob | null;
  meta: MetaPrueba | undefined;
  identidad: IdentidadAgente;
  onCerrar: () => void;
}) {
  const corriendo = !prueba || prueba.status === 'queued' || prueba.status === 'running';
  const progreso = typeof prueba?.detail?.insumo?.progreso === 'string' ? prueba.detail.insumo.progreso : null;
  const [fraseIdx, setFraseIdx] = useState(0);

  useEffect(() => {
    if (!corriendo) return;
    const t = setInterval(() => setFraseIdx((i) => (i + 1) % FRASES_TRABAJO.length), 2400);
    return () => clearInterval(t);
  }, [corriendo]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !corriendo) onCerrar();
    };
    window.addEventListener('keydown', onKey);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflowPrevio;
    };
  }, [corriendo, onCerrar]);

  // Mini-etapas de la prueba: Insumo → Trabajo → Salida
  const etapa = !prueba || prueba.status === 'queued' ? 0 : prueba.stage === 'agente' ? 1 : prueba.status === 'done' ? 3 : prueba.status === 'error' ? -1 : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
      <div className="fixed inset-0 bg-azul-noche/50" onClick={() => { if (!corriendo) onCerrar(); }} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Prueba de ${identidad.nombre}`}
        className="relative flex max-h-[92dvh] w-full max-w-2xl flex-col rounded-t-2xl border border-slate-200 bg-white shadow-xl sm:rounded-2xl"
      >
        {corriendo ? (
          <div className="flex flex-col items-center px-6 py-10 text-center sm:py-12">
            {/* Avatar del agente con anillo que respira */}
            <span className="relative flex h-20 w-20 items-center justify-center">
              <span aria-hidden="true" className="absolute inset-0 animate-ping rounded-2xl bg-celeste/30 [animation-duration:2.2s]" />
              <span aria-hidden="true" className={`relative flex h-16 w-16 items-center justify-center rounded-2xl text-lg font-semibold ring-2 ring-celeste/60 ${identidad.tinte}`}>
                {identidad.code}
              </span>
            </span>
            <p className="mt-5 text-base font-medium text-azul-noche">
              {identidad.nombre}, {identidad.oficio}
            </p>
            <p className="mt-1 text-sm font-normal text-gris" aria-live="polite">
              {!prueba || prueba.status === 'queued'
                ? 'está tomando la tarea…'
                : prueba.stage === 'agente'
                  ? `${FRASES_TRABAJO[fraseIdx]}${progreso?.startsWith('lote') ? ` (${progreso})` : ''}…`
                  : `Víctor está trayendo el insumo real${progreso ? ` (${progreso})` : ''}…`}
            </p>
            {/* Puntos de pensamiento */}
            <span className="mt-4 flex items-center gap-1.5" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-2 w-2 animate-bounce rounded-full bg-gradient-to-br from-azul-profundo to-celeste"
                  style={{ animationDelay: `${i * 0.18}s` }}
                />
              ))}
            </span>
            {/* Mini-etapas */}
            <ol className="mt-7 flex items-center" aria-label="Etapas de la prueba">
              {['Insumo', 'Trabajo', 'Salida'].map((label, i) => {
                const estado = i < etapa ? 'done' : i === etapa ? 'activo' : 'pendiente';
                return (
                  <li key={label} className="flex items-center">
                    <div className="flex flex-col items-center gap-1">
                      <span
                        className={`relative flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-medium transition-colors duration-300 ${
                          estado === 'done'
                            ? 'bg-azul-profundo text-white'
                            : estado === 'activo'
                              ? 'bg-white text-azul-profundo ring-2 ring-azul-profundo'
                              : 'bg-slate-100 text-gris'
                        }`}
                      >
                        {estado === 'done' ? (
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="m5 13 4 4L19 7" />
                          </svg>
                        ) : (
                          i + 1
                        )}
                        {estado === 'activo' && (
                          <span aria-hidden="true" className="absolute -bottom-1 h-[3px] w-4 rounded-full bg-gradient-to-r from-azul-profundo to-celeste" />
                        )}
                      </span>
                      <span className={`text-[10px] font-medium ${estado === 'pendiente' ? 'text-gris' : 'text-azul-noche'}`}>{label}</span>
                    </div>
                    {i < 2 && <span aria-hidden="true" className={`mx-3 mb-4 h-px w-10 sm:w-16 ${i < etapa ? 'bg-azul-profundo' : 'bg-slate-200'}`} />}
                  </li>
                );
              })}
            </ol>
            <p className="mt-6 text-[11px] font-normal text-gris">
              Una prueba toma entre 10 y 90 segundos según la fuente y el modelo.
            </p>
            <button type="button" onClick={onCerrar} className="btn-fantasma mt-2 !text-xs">
              Ocultar — la prueba sigue y el resultado queda en el panel
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 px-6 pb-0 pt-6">
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className={`flex h-10 w-10 items-center justify-center rounded-xl text-[13px] font-semibold ${identidad.tinte}`}>
                  {identidad.code}
                </span>
                <div>
                  <h2 className="text-base font-medium tracking-tight text-azul-noche">
                    {prueba.status === 'done' ? 'Prueba completada' : 'La prueba falló'}
                  </h2>
                  <p className="text-xs font-normal text-gris">{identidad.nombre}, {identidad.oficio} ({identidad.code})</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onCerrar}
                aria-label="Cerrar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-profundo focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-3 overflow-y-auto px-6 py-5">
              {prueba.status === 'error' && <p role="alert" className="alerta-error">{prueba.error ?? 'La prueba falló.'}</p>}
              <ResultadoPrueba prueba={prueba} meta={meta} />
              <div className="flex justify-end pt-1">
                <button type="button" onClick={onCerrar} className="btn-secundario">Cerrar</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Semáforo de prioridades ─────────────────────────────────────────────────
// El oficio de Marcela (N2) puede estructurar sus criterios con los marcadores
// [ALTA] / [MEDIA] / [BAJA]; aquí se editan como panales visuales. El texto que
// precede a [ALTA] son las reglas generales del oficio.
interface Semaforo {
  pre: string;
  alta: string;
  media: string;
  baja: string;
}

const RE_SEMAFORO = /^([\s\S]*?)\[ALTA\]\s*\n?([\s\S]*?)\n?\[MEDIA\]\s*\n?([\s\S]*?)\n?\[BAJA\]\s*\n?([\s\S]*)$/;

function parseSemaforo(t: string): Semaforo | null {
  const m = t.match(RE_SEMAFORO);
  if (!m) return null;
  return { pre: (m[1] ?? '').trim(), alta: (m[2] ?? '').trim(), media: (m[3] ?? '').trim(), baja: (m[4] ?? '').trim() };
}

function armarSemaforo(s: Semaforo): string {
  return `${s.pre}\n\n[ALTA]\n${s.alta}\n\n[MEDIA]\n${s.media}\n\n[BAJA]\n${s.baja}`;
}

const NIVELES_SEMAFORO = [
  {
    campo: 'alta' as const,
    titulo: 'ALTA',
    bajada: 'Urgente — destacado en la alerta del día',
    panel: 'border-red-200 bg-red-50/50',
    chip: 'bg-[#FDECEC] text-[#B42318] ring-[#F3C1BB]',
  },
  {
    campo: 'media' as const,
    titulo: 'MEDIA',
    bajada: 'Relevante — sale en la alerta del día y en el boletín semanal',
    panel: 'border-amber-200 bg-amber-50/50',
    chip: 'bg-[#FFF4E0] text-[#9A6700] ring-[#F0D9A8]',
  },
  {
    campo: 'baja' as const,
    titulo: 'BAJA',
    bajada: 'Solo titular en "Otros movimientos del día"',
    panel: 'border-slate-200 bg-slate-50',
    chip: 'bg-slate-100 text-slate-600 ring-slate-200',
  },
];

function SemaforoPrioridades({
  valor,
  editable,
  onCambio,
}: {
  valor: Semaforo;
  editable: boolean;
  onCambio?: (s: Semaforo) => void;
}) {
  return (
    <div className="mt-4 flex flex-col gap-3">
      {NIVELES_SEMAFORO.map((n) => (
        <div key={n.campo} className={`rounded-xl border p-4 ${n.panel}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${n.chip}`}>
              {n.titulo}
            </span>
            <span className="text-xs font-normal text-gris">{n.bajada}</span>
          </div>
          {editable ? (
            <textarea
              value={valor[n.campo]}
              onChange={(e) => onCambio?.({ ...valor, [n.campo]: e.target.value })}
              rows={4}
              aria-label={`Criterios ${n.titulo}`}
              className="campo mt-3 !h-auto resize-y bg-white/80 !text-[13px] leading-relaxed"
            />
          ) : (
            <p className="mt-2 whitespace-pre-wrap text-[13px] font-normal leading-relaxed text-texto">{valor[n.campo]}</p>
          )}
        </div>
      ))}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gris">Reglas generales del oficio</p>
        {editable ? (
          <textarea
            value={valor.pre}
            onChange={(e) => onCambio?.({ ...valor, pre: e.target.value })}
            rows={5}
            aria-label="Reglas generales del oficio"
            className="campo mt-2 !h-auto resize-y !text-[13px] leading-relaxed"
          />
        ) : (
          <p className="mt-2 whitespace-pre-wrap text-[13px] font-normal leading-relaxed text-texto">{valor.pre}</p>
        )}
      </div>
    </div>
  );
}

export default function SeccionEntrenamiento() {
  const [agentes, setAgentes] = useState<AgenteEnt[]>([]);
  const [contratosEditables, setContratosEditables] = useState(false);
  const [insumos, setInsumos] = useState<Insumos>({ fuentes: [], items_recientes: [], items_clasificados: [], items_con_borrador: [] });
  const [cargando, setCargando] = useState(true);
  const [sel, setSel] = useState('N1');
  // Ventanilla única (Emilia A6): chat conversacional para diagnosticar y ajustar el equipo.
  const [emiliaFav, setEmiliaFav] = useState<{ id: string; nombre: string } | null>(null);
  const [emiliaAbierta, setEmiliaAbierta] = useState(false);
  const abrirEmilia = useCallback(async () => {
    try {
      const fav = emiliaFav ?? (await apiFetch<{ favorito: { id: string; nombre: string } }>('/api/dia/newsletter-config')).favorito;
      setEmiliaFav(fav);
      setEmiliaAbierta(true);
    } catch { /* silencioso */ }
  }, [emiliaFav]);
  const [tipo, setTipo] = useState<Tipo>('brief');
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmarActivar, setConfirmarActivar] = useState(false);
  const [activadaMsg, setActivadaMsg] = useState<string | null>(null);
  const [activando, setActivando] = useState(false);
  // Banco de pruebas
  const [insumoSel, setInsumoSel] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [prueba, setPrueba] = useState<TestJob | null>(null);
  const [modalPrueba, setModalPrueba] = useState(false);
  const [probando, setProbando] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cargar = useCallback(async () => {
    try {
      const [ent, ins] = await Promise.all([
        apiFetch<{ agentes: AgenteEnt[]; contratos_editables: boolean }>('/api/entrenamiento'),
        apiFetch<Insumos>('/api/entrenamiento/insumos'),
      ]);
      setAgentes(ent.agentes.filter((a) => a.agent_code.startsWith('N')));
      setContratosEditables(ent.contratos_editables);
      setInsumos(ins);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el entrenamiento.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [cargar]);

  const ag = agentes.find((a) => a.agent_code === sel) ?? null;
  const id = agente(sel);

  const { activa, trabajo, textoActivo } = useMemo(() => {
    const vs = (ag?.versiones ?? []).filter((v) => v.tipo === tipo);
    const act = vs.find((v) => v.estado === 'activa') ?? null;
    const trab = vs.find((v) => v.estado === 'borrador' || v.estado === 'probada') ?? null;
    const base = tipo === 'brief' ? (ag?.context_brief ?? '') : (ag?.contrato_base ?? '');
    return { activa: act, trabajo: trab, textoActivo: act?.contenido ?? base };
  }, [ag, tipo]);

  const bloqueado = tipo === 'contrato' && !contratosEditables;

  // Al cambiar de agente/tipo se sale del modo edición y se limpia la prueba en curso.
  function cambiarSeleccion(nuevoSel: string, nuevoTipo?: Tipo) {
    setSel(nuevoSel);
    if (nuevoTipo) setTipo(nuevoTipo);
    setEditando(false);
    setError(null);
    setActivadaMsg(null);
    setPrueba(null);
    setProbando(false);
    setModalPrueba(false);
    setInsumoSel('');
    setMensaje('');
    if (pollRef.current) clearInterval(pollRef.current);
  }

  function empezarEdicion() {
    setTexto(trabajo?.contenido ?? textoActivo);
    setNota(trabajo?.nota ?? '');
    setEditando(true);
  }

  async function guardar() {
    if (!ag) return;
    setGuardando(true);
    setError(null);
    try {
      if (trabajo) {
        await apiFetch(`/api/entrenamiento/versiones/${trabajo.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ contenido: texto, nota: nota || null }),
        });
      } else {
        await apiFetch('/api/entrenamiento/versiones', {
          method: 'POST',
          body: JSON.stringify({ agent_code: ag.agent_code, tipo, contenido: texto, nota: nota || null }),
        });
      }
      setEditando(false);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  async function eliminarTrabajo() {
    if (!trabajo) return;
    setGuardando(true);
    setError(null);
    try {
      await apiFetch(`/api/entrenamiento/versiones/${trabajo.id}`, { method: 'DELETE' });
      setEditando(false);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo eliminar.');
    } finally {
      setGuardando(false);
    }
  }

  async function activar() {
    if (!trabajo) return;
    setActivando(true);
    setError(null);
    try {
      await apiFetch(`/api/entrenamiento/versiones/${trabajo.id}/activar`, {
        method: 'POST',
        body: JSON.stringify({ confirmar: true }),
      });
      setConfirmarActivar(false);
      // La garantía real: los agentes leen su entrenamiento desde la base al inicio de
      // cada corrida — activar ES re-entrenar. Se dice explícito para dar confianza.
      setActivadaMsg(
        `v${trabajo.version} quedó activa. La próxima corrida de ${ag?.display_name ?? id.nombre} la usará — los agentes leen su entrenamiento al inicio de cada corrida, sin reinicios ni pasos extra.`,
      );
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No se pudo activar.');
      setConfirmarActivar(false);
    } finally {
      setActivando(false);
    }
  }

  async function probar() {
    if (!ag || ag.agent_code === 'N5' || ag.agent_code === 'A1') return;
    setProbando(true);
    setPrueba(null);
    setModalPrueba(true);
    setError(null);
    const insumo =
      ag.agent_code === 'N1'
        ? { source_id: insumoSel }
        : ag.agent_code === 'N2'
          ? // Marcela clasifica EXACTAMENTE los 3 items mostrados (nunca data invisible).
            { item_ids: insumos.items_recientes.map((i) => i.id) }
          : { item_id: insumoSel };
    try {
      const { job } = await apiFetch<{ job: { id: string } }>('/api/entrenamiento/probar', {
        method: 'POST',
        body: JSON.stringify({ agent_code: ag.agent_code, version_id: trabajo?.id ?? null, insumo, mensaje: mensaje.trim() || null }),
      });
      const poll = async () => {
        try {
          const d = await apiFetch<{ job: TestJob }>(`/api/newsletter/jobs/${job.id}`);
          setPrueba(d.job);
          if (d.job.status === 'done' || d.job.status === 'error') {
            if (pollRef.current) clearInterval(pollRef.current);
            setProbando(false);
            await cargar(); // el éxito avanza el stepper (borrador → probada)
          }
        } catch {
          /* reintenta al próximo tick */
        }
      };
      void poll();
      pollRef.current = setInterval(() => void poll(), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo encolar la prueba.');
      setProbando(false);
      setModalPrueba(false);
    }
  }

  if (cargando) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner tamano="md" />
      </div>
    );
  }

  // Cada agente prueba con SUS 3 insumos más nuevos del sistema (lo mostrado = lo probado).
  const itemsParaAgente =
    sel === 'N4' ? insumos.items_con_borrador : sel === 'N3' ? insumos.items_clasificados : insumos.items_recientes;
  const insumoListo = sel === 'N2' || insumoSel !== '';
  const meta = prueba?.detail?.result?.meta;
  const versionesTipo = (ag?.versiones ?? []).filter((v) => v.tipo === tipo);

  return (
    <div className="flex flex-col gap-5">
      {/* Ventanilla única — Emilia, la Entrenadora (A6): configurar el Newsletter conversando */}
      <button
        type="button"
        onClick={() => void abrirEmilia()}
        className="group flex items-center gap-4 rounded-2xl border border-celeste/60 bg-gradient-to-br from-white to-[#F2F9FD] p-4 text-left transition-shadow hover:shadow-md"
      >
        <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-azul-profundo text-lg text-white">💬</span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[14px] font-medium text-azul-noche">Habla con Emilia, la Entrenadora</span>
            <span aria-hidden="true" className="text-[13px] font-medium text-azul-profundo group-hover:underline">→</span>
          </span>
          <span className="mt-0.5 block text-[12.5px] font-normal leading-relaxed text-gris">
            ¿Una noticia no salió y debería? ¿Quieres cambiar qué entra al Newsletter? Cuéntaselo en palabras simples y ella diagnostica el porqué y ajusta al equipo.
          </span>
        </span>
      </button>

      {/* Fila de agentes: el pipeline como equipo, con micro-stepper del ciclo */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {/* Víctor, el Director: orquestador determinista (código, no LLM) */}
        <button
          type="button"
          onClick={() => cambiarSeleccion('ORQ')}
          aria-pressed={sel === 'ORQ'}
          className={`flex min-w-[150px] shrink-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo ${
            sel === 'ORQ' ? 'border-azul-profundo bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-celeste'
          }`}
        >
          <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-azul-noche text-[11px] font-semibold text-white">
            V
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-medium leading-tight text-azul-noche">Víctor</span>
            <span className="block truncate text-[11px] font-normal leading-tight text-gris">el Director</span>
            <span className="mt-1 block text-[10px] font-normal text-gris">orquestador</span>
          </span>
        </button>
        {agentes.map((a) => {
          const idA = agente(a.agent_code);
          const vs = a.versiones;
          const trab = vs.find((v) => v.tipo === 'brief' && (v.estado === 'borrador' || v.estado === 'probada')) ??
            vs.find((v) => v.tipo === 'contrato' && (v.estado === 'borrador' || v.estado === 'probada')) ?? null;
          const act = vs.find((v) => v.estado === 'activa') ?? null;
          const activo = sel === a.agent_code;
          return (
            <button
              key={a.agent_code}
              type="button"
              onClick={() => cambiarSeleccion(a.agent_code)}
              aria-pressed={activo}
              className={`flex min-w-[150px] shrink-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo ${
                activo ? 'border-azul-profundo bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-celeste'
              }`}
            >
              <span aria-hidden="true" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold ${idA.tinte}`}>
                {a.agent_code}
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-medium leading-tight text-azul-noche">{a.display_name ?? idA.nombre}</span>
                <span className="block truncate text-[11px] font-normal leading-tight text-gris">{a.oficio ?? idA.oficio}</span>
                <span className="mt-1 flex items-center gap-1.5">
                  <MicroCiclo trabajo={trab} hayActiva={!!act} />
                  {act && <span className="text-[10px] font-normal text-gris">v{act.version} activa</span>}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {sel === 'ORQ' && (
        <div className="tarjeta p-5">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-medium text-azul-noche">
              Víctor, el Director <span className="font-normal text-gris">(orquestador)</span>
            </p>
            <span className="chip bg-slate-100 text-slate-700 ring-slate-300">Determinista · no se entrena aquí</span>
          </div>
          <p className="mt-2 max-w-3xl text-[13px] font-normal leading-relaxed text-texto">
            Víctor no es un agente LLM: es el director del pipeline, escrito en código. Descarga
            las fuentes con herramientas (HTTP/Playwright/PDF — nunca un modelo), guarda cada
            snapshot como fuente de verdad, comanda la cadena Nicanor → Marcela → Violeta ⇄
            Gabriela → Pablo, aplica el ciclo de reparación (máximo 2 intentos y escala a
            revisión humana) y reporta cada etapa en vivo.
          </p>
          <p className="mt-2 max-w-3xl text-xs font-normal leading-relaxed text-gris">
            Por diseño su flujo es determinista (regla de oro del proyecto), así que no tiene
            oficio ni contrato entrenables: su trabajo se audita en cada corrida. Puedes verlo
            dirigir en Ediciones → Generar → "Ver corrida en vivo".
          </p>
        </div>
      )}

      {ag && (
        <>
          {/* Ciclo de entrenamiento del agente seleccionado */}
          <div className="tarjeta overflow-x-auto p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-azul-noche">
                  {ag.display_name ?? id.nombre}, {ag.oficio ?? id.oficio} <span className="font-normal text-gris">({ag.agent_code})</span>
                </p>
                <p className="mt-0.5 text-xs font-normal text-gris">
                  Cerebro: {ag.model ?? 'sin modelo'} · configurable por el admin en Setup LLMs
                </p>
              </div>
              {/* Selector de capa: oficio (brief) vs contrato base */}
              <div role="tablist" aria-label="Capa de conocimiento" className="flex rounded-full bg-slate-100 p-0.5">
                {(['brief', 'contrato'] as const).map((t) => (
                  <button
                    key={t}
                    role="tab"
                    type="button"
                    aria-selected={tipo === t}
                    onClick={() => cambiarSeleccion(sel, t)}
                    className={`pill !h-7 ${tipo === t ? 'bg-white text-azul-profundo shadow-sm' : 'text-gris hover:text-azul-noche'}`}
                  >
                    {t === 'brief' ? 'Oficio (brief)' : 'Contrato base'}
                  </button>
                ))}
              </div>
            </div>
            <StepperCiclo trabajo={trabajo} hayActiva={!!activa} />
          </div>

          {/* Confirmación de activación: activar ES re-entrenar (lectura por corrida). */}
          {activadaMsg && (
            <div className="tarjeta flex items-start gap-3 border border-emerald-200 bg-emerald-50/70 p-4" role="status">
              <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">✓</span>
              <p className="text-sm font-normal leading-relaxed text-emerald-900">{activadaMsg}</p>
            </div>
          )}

          {/* Cambios a medias: sin Activar, la próxima corrida NO los usa. */}
          {trabajo && !activadaMsg && (
            <div className="tarjeta flex items-start gap-3 border border-amber-200 bg-amber-50/70 p-4" role="status">
              <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-medium text-amber-700">!</span>
              <p className="text-sm font-normal leading-relaxed text-amber-900">
                Tienes la versión v{trabajo.version} en trabajo ({trabajo.estado === 'probada' ? 'probada, lista para activar' : 'borrador'}) —{' '}
                <strong className="font-medium">la próxima corrida NO la usará</strong> hasta que la actives.
                Mientras tanto {ag.display_name ?? id.nombre} sigue trabajando con {activa ? `la v${activa.version} activa` : 'su conocimiento base'}.
              </p>
            </div>
          )}

          {error && <p role="alert" className="alerta-error">{error}</p>}

          <div className="grid gap-5 lg:grid-cols-2">
            {/* Zona Conocimiento */}
            <div className="tarjeta flex flex-col p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-azul-noche">
                  {tipo === 'brief' ? 'Oficio' : 'Contrato base'}
                  {trabajo && !editando && <span className={`chip ml-2 ${ESTADO_CHIP[trabajo.estado]}`}>v{trabajo.version} · {ESTADO_LABEL[trabajo.estado]}</span>}
                  {!trabajo && !editando && (
                    <span className={`chip ml-2 ${activa ? ESTADO_CHIP.activa : 'bg-slate-100 text-slate-700 ring-slate-300'}`}>
                      {activa ? `v${activa.version} activa` : tipo === 'contrato' ? 'Base (código)' : ag.context_brief ? 'Semilla' : 'Vacío'}
                    </span>
                  )}
                </p>
                {!editando && !bloqueado && (
                  <button type="button" onClick={empezarEdicion} className="btn-secundario !h-8 !px-3.5 !text-[13px]">
                    {trabajo ? 'Seguir editando' : 'Crear versión de trabajo'}
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs font-normal leading-relaxed text-gris">
                {tipo === 'brief'
                  ? 'Cómo piensa este agente para TODAS las fuentes: criterios, énfasis, línea editorial. Lo que aplica a una sola fuente va en Fuentes → Editar.'
                  : 'Identidad y reglas duras del agente. Activar un contrato exige prueba previa; la validación con evals llega en Fase 2.'}
              </p>
              {bloqueado && (
                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-normal text-amber-800 ring-1 ring-inset ring-amber-200">
                  La edición de contratos base está deshabilitada (contratos_editables = false). El texto se muestra solo para lectura.
                </p>
              )}

              {/* La card siempre muestra lectura; la edición vive en un modal bloqueante
                  (decisión 2026-07-14: espacio real para redactar, sin cierres accidentales). */}
              {(() => {
                const contenido = trabajo?.contenido ?? textoActivo;
                const sem = tipo === 'brief' ? parseSemaforo(contenido) : null;
                if (sem) return <SemaforoPrioridades valor={sem} editable={false} />;
                return (
                  <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-[#FAFBFC] p-4 font-mono text-[11px] leading-relaxed text-texto ring-1 ring-inset ring-slate-200">
                    {contenido || 'Este agente aún no tiene oficio definido. Crea una versión de trabajo para entrenarlo.'}
                  </pre>
                );
              })()}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {trabajo && (
                  <>
                    <button
                      type="button"
                      onClick={() => setConfirmarActivar(true)}
                      disabled={trabajo.estado !== 'probada'}
                      title={trabajo.estado !== 'probada' ? 'Prueba esta versión al menos una vez antes de activarla' : undefined}
                      className="btn-primario disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Activar v{trabajo.version}
                    </button>
                    <button type="button" onClick={() => void eliminarTrabajo()} disabled={guardando} className="btn-fantasma text-red-600 hover:bg-red-50">
                      Descartar borrador
                    </button>
                  </>
                )}
              </div>

              {/* Historial de versiones */}
              {versionesTipo.length > 0 && (
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <p className="text-xs font-medium text-azul-noche">Historial</p>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {versionesTipo.map((v) => (
                      <li key={v.id} className="flex flex-wrap items-center gap-2 text-xs font-normal text-gris">
                        <span className={`chip ${ESTADO_CHIP[v.estado]}`}>v{v.version} · {ESTADO_LABEL[v.estado]}</span>
                        <span>{fmtFecha(v.created_at)}</span>
                        {v.autor_email && <span className="font-medium text-azul-noche">· {v.autor_email}</span>}
                        {v.nota && <span className="truncate text-texto">— {v.nota}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Zona Banco de pruebas */}
            <div className="tarjeta flex flex-col p-5">
              <p className="text-sm font-medium text-azul-noche">Banco de pruebas</p>
              <p className="mt-1 text-xs font-normal leading-relaxed text-gris">
                Corre a {ag.display_name ?? id.nombre} en aislado con un insumo real
                {trabajo ? <> usando la versión <span className="font-medium text-azul-noche">v{trabajo.version}</span> en trabajo</> : ' con su conocimiento activo'}
                . No toca la configuración activa ni crea ediciones.
              </p>

              {sel === 'A1' ? (
                <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2.5 text-xs font-normal text-gris ring-1 ring-inset ring-slate-200">
                  Andrés se prueba CONVERSANDO: edita su oficio aquí, actívalo, y hazle preguntas reales en Agentes → DIA sobre una carpeta favorita. Su banco de pruebas dorado (preguntas con respuesta verificada desde RCAs) llega en la Fase 5 del plan de entrenamiento.
                </p>
              ) : sel === 'N5' ? (
                <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2.5 text-xs font-normal text-gris ring-1 ring-inset ring-slate-200">
                  Pablo compone la edición completa, así que se prueba con el Ensayo general del flujo (Fase 3). Mientras tanto puedes editar su oficio y contrato.
                </p>
              ) : (
                <>
                  <label className="etiqueta mt-4" htmlFor="ent-insumo">Insumo real</label>
                  {sel === 'N1' ? (
                    <select id="ent-insumo" value={insumoSel} onChange={(e) => setInsumoSel(e.target.value)} className="campo mt-1">
                      <option value="">Elige una fuente oficial…</option>
                      {insumos.fuentes.map((f) => (
                        <option key={f.id} value={f.id}>{f.code} — {f.name}</option>
                      ))}
                    </select>
                  ) : sel === 'N2' ? (
                    <div id="ent-insumo" className="mt-1 rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-inset ring-slate-200">
                      {insumos.items_recientes.length === 0 ? (
                        <p className="text-xs font-normal text-texto">Aún no hay items en el sistema — corre primero una prueba de Nicanor o una corrida completa.</p>
                      ) : (
                        <>
                          <p className="text-xs font-medium text-azul-noche">Marcela clasificará los {insumos.items_recientes.length} items más nuevos del sistema:</p>
                          <ul className="mt-1.5 flex flex-col gap-1">
                            {insumos.items_recientes.map((i) => (
                              <li key={i.id} className="truncate text-xs font-normal text-texto" title={i.title ?? ''}>
                                <span aria-hidden="true" className="text-azul-medio">›</span> {(i.title ?? 'Sin título').slice(0, 90)}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <select id="ent-insumo" value={insumoSel} onChange={(e) => setInsumoSel(e.target.value)} className="campo mt-1">
                        <option value="">
                          {itemsParaAgente.length === 0
                            ? sel === 'N4' ? 'No hay items con borrador de Violeta aún…' : 'No hay items clasificados aún…'
                            : 'Elige un item real…'}
                        </option>
                        {itemsParaAgente.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.priority ? `[${i.priority}] ` : ''}{(i.title ?? 'Sin título').slice(0, 80)}
                          </option>
                        ))}
                      </select>
                      {itemsParaAgente.length > 0 && (
                        <p className="mt-1 text-[11px] font-normal text-gris">
                          Los {itemsParaAgente.length} {sel === 'N4' ? 'borradores' : 'items clasificados'} más nuevos del sistema.
                        </p>
                      )}
                    </>
                  )}

                  <label className="etiqueta mt-3" htmlFor="ent-mensaje">Mensaje de prueba (opcional)</label>
                  <textarea
                    id="ent-mensaje"
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder={
                      sel === 'N1'
                        ? `p. ej. Fíjate en las noticias del ${fechaAyer()} y prioriza las de la SMA`
                        : `p. ej. ${sel === 'N2' ? 'Sé más estricta con los actos particulares' : sel === 'N3' ? 'Acorta el titular y parte por la implicancia' : 'Sé más exigente con los plazos citados'}`
                    }
                    className="campo mt-1 !h-auto resize-y leading-relaxed"
                  />
                  <p className="mt-1 text-[11px] font-normal leading-relaxed text-gris">
                    Instrucción efímera solo para esta prueba: no se guarda en ninguna versión.
                    {sel === 'N1' && ' Si mencionas una fecha o un período ("10 de julio", "todo julio", "del 1 al 15 de julio"), Víctor trae esas ediciones/páginas y Nicanor lee el período completo.'}
                  </p>

                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => void probar()}
                      disabled={probando || !insumoListo || (sel === 'N2' && insumos.items_recientes.length === 0)}
                      className="btn-primario disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {probando ? (<><Spinner tamano="xs" className="mr-2" />Probando…</>) : `Probar a ${ag.display_name ?? id.nombre}`}
                    </button>
                  </div>

                  {/* Resultado */}
                  {prueba && (
                    <div className="mt-4 flex flex-col gap-3 rounded-[10px] border border-slate-200 bg-[#FAFBFC] p-4">
                      {(prueba.status === 'queued' || prueba.status === 'running') && (
                        <p className="flex items-center gap-2 text-xs font-normal text-gris">
                          <Spinner tamano="xs" />
                          {prueba.status === 'queued'
                            ? 'En cola, el worker la toma en segundos…'
                            : prueba.stage === 'agente'
                              ? `${ag.display_name ?? id.nombre} está ${ETAPA_PRUEBA.agente}`
                              : (ETAPA_PRUEBA[prueba.stage ?? ''] ?? 'Trabajando…')}
                        </p>
                      )}
                      {prueba.status === 'error' && <p role="alert" className="alerta-error">{prueba.error ?? 'La prueba falló.'}</p>}
                      {prueba.status === 'done' && (
                        <>
                          <ResultadoPrueba prueba={prueba} meta={meta} />
                          <button type="button" onClick={() => setModalPrueba(true)} className="btn-fantasma self-start !text-xs">
                            Ver en grande
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal bloqueante de la prueba: agente trabajando → resultado completo */}
      {modalPrueba && (
        <ModalPrueba prueba={prueba} meta={meta} identidad={id} onCerrar={() => setModalPrueba(false)} />
      )}

      {/* Confirmación de activación (la validación automática llega en Fase 2) */}
      {/* Modal bloqueante de redacción: solo se cierra con Guardar o Cancelar. */}
      {editando && ag && (
        <Modal
          titulo={`${trabajo ? `Editar v${trabajo.version}` : 'Nueva versión'} — ${tipo === 'brief' ? 'Oficio' : 'Contrato base'} de ${ag.display_name ?? id.nombre}`}
          onCerrar={() => setEditando(false)}
          bloqueante
          ancho="xl"
        >
          {(() => {
            const sem = tipo === 'brief' ? parseSemaforo(texto) : null;
            if (sem) {
              return (
                <div className="max-h-[60vh] overflow-y-auto pr-1">
                  <SemaforoPrioridades valor={sem} editable onCambio={(s) => setTexto(armarSemaforo(s))} />
                </div>
              );
            }
            return (
              <>
                <label className="etiqueta" htmlFor="ent-texto">Contenido</label>
                <textarea
                  id="ent-texto"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={18}
                  className="campo mt-1 !h-auto max-h-[55vh] resize-y font-mono !text-[13px] leading-relaxed"
                />
              </>
            );
          })()}
          <label className="etiqueta mt-4" htmlFor="ent-nota">Nota de la versión (qué cambiaste y por qué)</label>
          <input id="ent-nota" value={nota} onChange={(e) => setNota(e.target.value)} className="campo mt-1" placeholder="p. ej. Sube prioridad de consultas públicas con plazo < 15 días" />
          {trabajo?.estado === 'probada' && (
            <p className="mt-2 text-[11px] font-normal text-amber-700">Si cambias el contenido, la versión vuelve a Borrador: lo probado ya no sería lo que hay.</p>
          )}
          <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" onClick={() => setEditando(false)} disabled={guardando} className="btn-fantasma">Cancelar</button>
            <button type="button" onClick={() => void guardar()} disabled={guardando || !texto.trim()} className="btn-primario">
              {guardando ? 'Guardando…' : trabajo ? `Guardar v${trabajo.version}` : 'Guardar como nueva versión'}
            </button>
          </div>
        </Modal>
      )}

      {confirmarActivar && trabajo && (
        <Modal titulo={`Activar v${trabajo.version} de ${ag?.display_name ?? id.nombre}`} onCerrar={() => setConfirmarActivar(false)}>
          <p className="text-sm font-normal leading-relaxed text-texto">
            {tipo === 'brief'
              ? 'El nuevo oficio reemplaza al vigente y aplica desde la próxima corrida.'
              : 'Vas a reemplazar el contrato base de este agente. Es la capa de identidad y reglas duras: revisa la prueba con calma antes de confirmar.'}
          </p>
          <p className="mt-2 text-xs font-normal leading-relaxed text-gris">
            La validación automática con casos dorados{tipo === 'contrato' ? ' y evals de control' : ''} llega en Fase 2; por ahora la activación es manual. Siempre puedes volver a la versión anterior desde el historial.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmarActivar(false)} disabled={activando} className="btn-fantasma">Cancelar</button>
            <button type="button" onClick={() => void activar()} disabled={activando} className="btn-primario">
              {activando ? 'Activando…' : 'Activar versión'}
            </button>
          </div>
        </Modal>
      )}

      {/* Chat de la ventanilla única (Emilia) — overlay grande, reusa ChatAndres */}
      {emiliaAbierta && emiliaFav && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-5">
          <div className="fixed inset-0 bg-azul-noche/50" onClick={() => setEmiliaAbierta(false)} aria-hidden="true" />
          <div role="dialog" aria-modal="true" aria-label="Emilia, la Entrenadora" className="relative flex h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
              <p className="text-[13px] font-medium text-azul-noche">💬 Emilia · la Entrenadora del Newsletter</p>
              <button type="button" onClick={() => setEmiliaAbierta(false)} aria-label="Cerrar" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gris transition-colors hover:bg-slate-100 hover:text-azul-profundo">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
              <ChatAndres
                favoritoId={emiliaFav.id}
                nombre="Configuración del Newsletter"
                agente="Emilia"
                sinCapitulos
                etiquetaMemoria="Memoria del Newsletter"
                onVolver={() => setEmiliaAbierta(false)}
                placeholderVacio="Cuéntale a Emilia qué revisar del Newsletter. Puede diagnosticar por qué una noticia salió o no, y ajustar el criterio del equipo — todo en palabras simples."
                badgesIniciales={[
                  'Hay una noticia que no salió y debería…',
                  'Quiero modificar lo que sale y no sale en el Newsletter…',
                  '¿Por qué salió una noticia que no correspondía?',
                  'Quiero agregar una fuente nueva al Newsletter…',
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
